import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { SketchPicker } from "react-color";
import ColorizeIcon from "@mui/icons-material/Colorize";

const PREFIX = "ColorPicker";

const classes = {
  root: `${PREFIX}-root`,
  picker: `${PREFIX}-picker`,
};

const Root = styled("div")({
  [`&.${classes.root}`]: {
    position: "relative",
  },
  [`& .${classes.picker}`]: {
    position: "absolute",
    top: 36,
    right: 0,
    left: 0,
    zIndex: 1000,
  },
});

interface ColorPickerProps {
  value?: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  inputProps?: Record<string, unknown>;
  label?: string;
  name?: string;
  variant?: "outlined" | "filled" | "standard";
  defaultValue?: string;
  [key: string]: unknown;
}

export function ColorPicker({
  value,
  onChange,
  disabled,
  error,
  fullWidth,
  helperText,
  inputProps,
  label,
  name,
  variant = "outlined",
  ...props
}: ColorPickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "color-picker-popover" : undefined;

  return (
    <Root className={classes.root}>
      <TextField
        {...inputProps}
        label={label}
        name={name}
        variant={variant}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        helperText={helperText}
        type="text"
        value={value || ""}
        onClick={handleClick}
        autoComplete="new-password"
        onChange={(event) => {
          const color = event.target.value;
          onChange(color);
        }}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment
                position="end"
                style={{ color: value || undefined }}
              >
                <ColorizeIcon />
              </InputAdornment>
            ),
          },
        }}
        {...props}
      />

      {open && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          style={{
            zIndex: 1500,
          }}
        >
          <SketchPicker
            className={classes.picker}
            color={value || ""}
            onChange={(color) => {
              if (color?.hex) {
                onChange(color.hex);
              }
            }}
          />
        </Popover>
      )}
    </Root>
  );
}
